import { useEffect, useState } from "react";
import type { AssistantReply, TaskStatus } from "@maestros/contracts";
import type { TaskDetail } from "../../api/types.ts";
import type { PlanningRun } from "../../api/types-wave5.ts";
import { api } from "../../api/client.ts";
import { useI18n } from "../../i18n/index.tsx";

type Ref = AssistantReply["refs"][number];
/** Reads durable work records while the conversation remains available; never starts another agent. */
export function BackgroundWork({ reference, live }: { reference: Ref; live: boolean }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<TaskStatus | PlanningRun["state"] | null>(null);
  const [number, setNumber] = useState<number | null>(null);
  const [observed, setObserved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const projectId = /^\/projects\/([0-9a-f-]{36})\//i.exec(reference.href)?.[1];
  useEffect(() => {
    if (!projectId || !["task", "planning_run"].includes(reference.type)) return;
    const controller = new AbortController(); let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      let terminal = false;
      try {
        const task = reference.type === "task";
        const row = await api<TaskDetail | PlanningRun>(task ? `/tasks/${reference.id}` : `/projects/${projectId}/planning-runs/${reference.id}`, { signal: controller.signal });
        if (row.id !== reference.id || row.project_id !== projectId) throw new Error("Work reference mismatch");
        if (controller.signal.aborted) return;
        const next = ("status" in row ? row.status : row.state) as TaskStatus | PlanningRun["state"];
        setStatus(next); setNumber("number" in row ? row.number : null); setObserved(new Date().toISOString()); setFailed(false);
        terminal = ["done", "cancelled", "completed", "failed"].includes(next);
      } catch { if (!controller.signal.aborted) setFailed(true); }
      if (live && !terminal && !controller.signal.aborted) timer = setTimeout(() => void refresh(), 5000);
    };
    void refresh();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [projectId, reference.id, reference.type, live]);
  const label = reference.type === "task" ? t("assistant.work.task") : reference.type === "planning_run" ? t("assistant.work.planner") : t("assistant.work.prd");
  const taskState = status && reference.type === "task" ? t(`status.${status as TaskStatus}`) : null;
  const planState = status && reference.type === "planning_run" ? t(`assistant.work.${status as PlanningRun["state"]}`) : null;
  const href = reference.type === "task" ? `${reference.href}?task=${encodeURIComponent(reference.id)}` : reference.href;
  return <li data-testid="assistant-background-work">
    <a href={href}>{label}{number !== null ? ` #${number}` : ""} · <code>{reference.id}</code></a>
    <span role="status">{failed ? ` · ${t("assistant.work.unavailable")}` : taskState || planState ? ` · ${taskState || planState}` : ""}</span>
    {observed ? <time className="assistant-note" dateTime={observed} title={observed}> · {t("assistant.work.observed")}</time> : null}
  </li>;
}
